<template>
  <div class="panorama noscrollbars">
    <LightboxGallery :showLightbox="lightboxContent.length > 0 && lightboxReady" :content="lightboxContent"
      :tileUrl="lightboxContent" @closed="closeLightbox()" tabindex="0" />

    <Marzipano v-if="startingPanorama && panoramas.length" :tileUrl="tileUrl"
      :multiple-scenes="[startingPanorama, ...panoramas]" :data="startingPanorama && startingPanorama.panorama"
      :ready="ready" :sasKey="sasKey" @walkthroughReady="walkthroughLoaded" @change-scene="handleChangeScene"
      @change-video-scene-active="handleChangeVideoSceneActive" @loaded="panoLoaded" @view="activeSceneView = $event"
      @scenes="handleScenes($event)" ref="marzipano" :minYaw="minYaw" :maxYaw="maxYaw"
      :rectConfigEnabled="rectConfigEnabled" :active-scene="activeScene" :active-scene-view="activeSceneView"
      :initialViewParameters="initialViewParameters" @initialViewParameters="initialViewParameters = $event"
      tabindex="-1">
      <div v-for="(child, i) in parsedPanoramaContent"
        :key="activeScene ? `${i}-${activeSceneIndex}-${videoSceneActiveKey}` : `${i}-${videoSceneActiveKey}`">
        <MarzipanoRectHotspot v-if="child.type === 'virtualroomcontent'" :index="i" :key="activeScene
            ? `panorama-${activeSceneIndex}-${startingPanorama.id}-${i}-${videoSceneActiveKey}`
            : `panorama-${i}-${videoSceneActiveKey}`
          " :yaw="child.position_data.yaw" :pitch="child.position_data.pitch" :rotX="child.position_data.rotX"
          :rotY="child.position_data.rotY" :rotZ="child.position_data.rotZ" :width="child.position_data.width"
          :height="child.position_data.height" :thumbnail="child.thumbnail"
          :primary_colour_override="child.primary_colour_override"
          :secondary_colour_override="child.secondary_colour_override" :scene="activeScene || scene"
          :activeSceneIndex="startingPanorama.id" :arialabel="getAriaLabel(child)" :title="getChildTitle(child)"
          :icon="child.menu_icon" :clickable="child.clickable" :transparent="child.transparent_background"
          :text="child.chat_bubble_text" :bubble_width="child.chat_bubble_width" :lightboxActive="lightboxActive"
          @click="clickChild(child, i)" @focus="onFocus()" :isMoving="isMoving" :movementSpeed="movementSpeed"
          :invisible="child.invisible" ref="rectHotspot" />
      </div>

      <div v-for="(child, i) in parsedPanoramaContent" :key="i + 'chat'">
        <MarzipanoBubbleHotspot v-if="child.type === 'chatbubblehotspot'" :index="i" :yaw="child.position_data.yaw"
          :pitch="child.position_data.pitch" :rotX="child.position_data.rotX" :rotY="child.position_data.rotY"
          :rotZ="child.position_data.rotZ" :width="child.position_data.width" :height="child.position_data.height"
          :text="child.chat_bubble_text" :thumbnail="child.thumbnail"
          :primary_colour_override="child.primary_colour_override"
          :secondary_colour_override="child.secondary_colour_override" :scene="scene" :arialabel="getAriaLabel(child)"
          :title="getChildTitle(child)" :icon="child.menu_icon" :clickable="child.clickable"
          :transparent="child.transparent_background" :lightboxActive="lightboxActive" @click="clickChild(child)"
          @focus="onFocus()" :isMoving="isMoving" :movementSpeed="movementSpeed" />
      </div>
    </Marzipano>
    <PanoramaLoading class="pano" v-if="walkthroughLoading" />

    <div class="button-panel pt-7 pr-5">
      <v-container class="pt-0 pb-0" style="display:flex; flex-direction: column; gap: 15px;">
        <v-row>
          <v-btn v-if="showLandingInfo" fab small class="use-mouse" @click="showLandingTrigger()"
            data-testid="info-btn">
            <v-icon>mdi-information-outline</v-icon>
          </v-btn>
        </v-row>
        <v-row v-if="hasVideoBackground">
          <v-btn fab small data-testid="video-background--btn" @click="toggleVideoSceneActive">
            <v-icon>{{
              videoSceneActive
                ? "mdi-video-box"
                : "mdi-video-box-off"
            }}</v-icon>
          </v-btn>
        </v-row>
      </v-container>
    </div>

    <PageHeaderCard :transparent="true"> </PageHeaderCard>

    <img v-if="secondaryLogo !== undefined" :src="secondaryLogo" class="secondary-logo-panel ignore-mouse" />

    <v-dialog :content-class="`accessibility-lightbox ${mobileBrowser}`" persistent scrollable
      v-model="accessibilityMode" ref="accessibilityLightbox">
      <template v-slot:default="dialog">
        <v-card height="100%" data-testid="walkthrough-accessibility-modal">
          <FocusLoop :is-visible="activeTrap">
            <v-card-title class="text-h5 accessibility-header lighten-2">
              Accessibility Mode
            </v-card-title>
            <v-tabs show-arrows>
              <v-tab>Explore the Room</v-tab>
              <v-tab-item>
                <v-list-item v-if="activeSceneIndex === 0" key="1" link class="accessibility-btn"
                  @click="showLandingTrigger()">Read Welcome Message</v-list-item>
                <v-list-item v-for="(child, i) in filteredPanoramaContent" :key="accessibilityFlag ? i + 1 : i" link
                  class="accessibility-btn" @click="clickChild(child)">{{ getAccessibilityChildTitle(child)
                  }}</v-list-item>
              </v-tab-item>
              <v-tab v-if="!$store.state.wagtail.siteSettings.hide_menu_mode">Site Navigation
              </v-tab>
              <v-tab-item>
                <v-list-item v-for="(menuItem, i) in siteStructure" :key="i" :to="menuItem.pathname" link>
                  <v-list-item-icon class="mr-4">
                    <v-icon>{{ menuItem.menu_icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ menuItem.title }}</v-list-item-title>
                </v-list-item></v-tab-item>
            </v-tabs>
            <v-divider></v-divider>
            <v-btn class="accessibility-btn close-accessibility-btn"
              @click="closeAccessibilityLightbox(), (dialog.value = false)">Close</v-btn>
          </FocusLoop>
        </v-card>
      </template>
    </v-dialog>
    <LandingStreamfield :pageType="'room'" ref="landing" :data="startingPanoDataResult" :key="startingPanoDataResult.id"
      v-if="showLandingStreamfield && !landingStreamfieldFlag" @closed="landingClosed()">
    </LandingStreamfield>
    <v-scale-transition origin="center center">
      <PanoramaInstructions v-show="showInstructions" />
    </v-scale-transition>
  </div>
</template>

<script>
import Marzipano from "../../components/panorama/Marzipano.vue";
import PanoramaLoading from "../../components/panorama/PanoramaLoading.vue";
import MarzipanoRectHotspot from "../../components/panorama/MarzipanoRectHotspot";
import MarzipanoBubbleHotspot from "../../components/panorama/MarzipanoBubbleHotspot.vue";
import PanoramaInstructions from "../../components/panorama/PanoramaInstructions";
import LandingStreamfield from "../../components/misc/LandingStreamfield.vue";
import PageHeaderCard from "../../components/page/pageHeaderCard.vue";
import { FocusLoop } from "@vue-a11y/focus-loop";
import * as arrowKeyNavigation from "arrow-key-navigation";

//enables next & previous tab style DOM navigation using arrow keys
arrowKeyNavigation.register();
import Cookies from "js-cookie";

export default {
  name: "EngageWalkthrough",
  props: ["pageData", "pageChildren", "accessibilityStream"],
  data: () => ({
    videoSceneActive: false,
    mobileBrowser: "",
    panorama: "/engagement_room.json",
    lightboxData: undefined,
    scene: undefined,
    lastYaw: undefined,
    lastPitch: undefined,
    lastFov: undefined,
    useLastLocation: false,
    lightboxReady: false,
    showLanding: false,
    showInstructions: false,
    instructionsShown: false,
    ready: false,
    consoleYaw: undefined,
    consolePitch: undefined,
    formYaw: undefined,
    formPitch: undefined,
    lightboxActive: undefined,
    currentFocusIndex: 0,
    currentActiveHotspot: undefined,
    currentActiveAccessibilityMenuItem: undefined,
    isMouseControl: false,
    isMoving: false,
    movementSpeed: 600,
    accessibilityModalFlag: false,
    accessibilityFlag: false,
    activeTrap: true,
    lightbox2Active: false,
    scenes: [],
    panoramas: [],
    childInstances: [],
    activeScene: null,
    activeSceneView: null,
    initialViewParameters: null,
    activeSceneIndex: null,
    selectedChild: null,
    startingPanorama: null,
    walkthroughLoading: true,
    startingPanoDataResult: null,
    showLandingStreamfield: false,
    hasLandingStreamfield: false,
    showLandingInfo: false,
    landingStreamfieldFlag: false,
    sortedHotspots: [],
    foundChildren: [],
  }),
  components: {
    Marzipano,
    MarzipanoRectHotspot,
    PanoramaInstructions,
    PanoramaLoading,
    // StreamfieldBlock,
    // PageShortcutBlock,
    PageHeaderCard,
    FocusLoop,
    MarzipanoBubbleHotspot,
    LandingStreamfield,
  },
  created() {
    this.$root.$refs.engagementRoom = this;
    this.activeSceneIndex = 0;
  },
  mounted: async function () {

    console.log("pageData", this.pageData)
    console.log("pageChildren", this.pageChildren)

    /* Start of Url Conversion */
    // Convert the url with slugs to an url with queries

    const params = new URLSearchParams(window.location.search);

    const cleanPathname = new URL(window.location.href).pathname.replace(
      /(\/)\1+/g,
      "/"
    );

    let paths = cleanPathname.replace("/", "");

    const slugs = paths.split("/").filter((item) => item !== "");

    // If multiple slugs are present, then construct the correct URL for the walkthrough room
    if (slugs.length > 1) {
      let walkthroughUrl = `${window.location.origin}/${slugs[0]}`;
      walkthroughUrl = walkthroughUrl + `?scene=/${slugs[1]}/`;

      if (slugs.length === 3) {
        walkthroughUrl = walkthroughUrl + `&hotspot=/${slugs[1]}/${slugs[2]}/`;
      }

      window.location.href = walkthroughUrl;
    }

    if (!params.get("scene") && !params.get("hotspot")) {
      const startingPano = this.pageData.panorama_child_content.filter(
        (pano) => pano.id === this.pageData.starting_pano.id
      )[0];

      const firstScene = new URL(startingPano.html_url).pathname;
      this.$router.push({ query: { scene: firstScene } });
    }
    /* End of Url Conversion */

    await this.fetchPanoramas(
      this.pageData.starting_pano.id,
      this.pageData.panorama_child_content
    );

    this.fetchAsyncData();


    if (this.streamfieldLoaded(null, true) === "true") {
      this.showLandingStreamfield = false;
    }

    console.log(this.parsedPanoramaContent, "page data from walkthrough");
    this.accessibilityFlagChecker();

    //Check if Guide and initialise
    if (this.startingPanorama.selected_guide_data && this.streamfieldLoaded(null, true) !== "true") {
      this.$store.dispatch("wagtail/toggleGuideEnabled");
      this.$store.dispatch(
        "wagtail/replaceGuideChildPages",
        this.startingPanorama.selected_guide_data.child_page_ids
      );
    }
  },

  computed: {

    roomGuideData() {
      return this.$store.state.wagtail.roomGuide;
    },

    videoSceneActiveKey() {
      return this.videoSceneActive ? 'video' : 'image'
    },

    hasVideoBackground() {
      return this.startingPanorama?.panorama.video;
    },

    parsedPanoramaContent() {
      if (this.startingPanorama) {
        return [
          ...this.startingPanorama.child_content,
          ...this.startingPanorama.chat_bubble_content,
        ];
      }
      return [];
    },
    filteredPanoramaContent() {
      this.sortByGoLive();
      return this.sortedHotspots.filter(
        (child) => this.getAccessibilityChildTitle(child) !== ""
      );
    },
    lightboxContent() {
      if (this.$store.state.wagtail.currentPopup === undefined) return [];
      else return this.$store.state.wagtail.currentPopup.content;
    },
    tileUrl() {
      return (
        this.startingPanorama.panorama &&
        this.startingPanorama.panorama.tiles.split("?")[0]
      );
    },
    currentPath() {
      return this.$route.path;
    },
    currentQueryHotspot() {
      return this.$route.query.hotspot;
    },
    sasKey() {
      return (
        this.startingPanorama.panorama && this.startingPanorama.panorama.sas_key
      );
    },
    siteStructure() {
      let siteStructure = [];
      for (const item of this.$store.state.wagtail.siteStructure) {
        //TODO: Unsure how reliable this is.  Looks at the URL to calculate depth by counting slashes in the URL
        item.depth = item.meta.html_url.split("/").length - 4;
        let url = new URL(item.meta.html_url);
        item.pathname = url.pathname;
        item.menutitle = Array(item.depth + 1).join(" -") + " " + item.title;
        siteStructure.push(item);
      }
      return siteStructure;
    },
    secondaryLogo() {
      return this.$store.state.wagtail.siteSettings.secondary_logo;
    },
    minYaw() {
      if (this.startingPanorama.panorama.min_yaw !== undefined)
        return this.startingPanorama.panorama.min_yaw;
      return -181;
    },
    maxYaw() {
      if (this.startingPanorama.panorama.max_yaw !== undefined)
        return this.startingPanorama.panorama.max_yaw;
      return 181;
    },
    rectConfigEnabled() {
      return this.startingPanorama.title === "panoconfig";
    },
    accessibilityMode() {
      return this.$store.state.wagtail.accessibilityMode;
    },
    islightboxActive() {
      if (this.$store.state.lightbox.lightboxObjects.length == 0) {
        return false;
      } else {
        return true;
      }
    },


  },
  methods: {

    sortByGoLive() {
      // Create a copy of the parsedPanoramaContent array and sort it (Used to control custom order of accessibility menu without impeeding DOM tab order)
      this.sortedHotspots = this.parsedPanoramaContent.slice().sort((a, b) => {
        const dateA = new Date(a.go_live || a.first_published_at);
        const dateB = new Date(b.go_live || b.first_published_at);

        // Sort in ascending order
        return dateA - dateB;
      });
    },
    streamfieldLoaded(value, get = false) {
      if (get) {
        return Cookies.get("streamfieldLoaded");
      }
      Cookies.set("streamfieldLoaded", value, { expires: 30 });
    },
    async fetchAsyncData() {
      try {
        const startingPanoData = await this.$store.dispatch(
          "wagtail/getPageById",
          this.startingPanorama.id
        );
        this.startingPanoDataResult = startingPanoData;
        if (this.startingPanoDataResult.landing_streamfield.length >= 1 && this.streamfieldLoaded(null, true) != "true") {
          this.showLandingStreamfield = true;
        } else {

          // Still show the instructions even when there is no landing streamfield
          this.displayInstructions();
        }

      } catch (error) {
        console.error("Error fetching pano data:", error);
        this.asyncData = this.$store.state.wagtail.currentPage; // Fallback data in case of error
        this.showLandingStreamfield = false;
        // Still show the instructions even when there is no landing streamfield
        this.displayInstructions();

      }
    },
    walkthroughLoaded() {
      this.walkthroughLoading = false;
    },

    toggleVideoSceneActive() {
      const newValue = !this.videoSceneActive
      this.videoSceneActive = newValue
      this.$refs.marzipano.emitNewScene(newValue)
    },

    handleChangeScene(scene) {
      this.activeScene = null
      this.scene = scene;
    },

    handleChangeVideoSceneActive(active) {
      this.videoSceneActive = active
    },

    async handleScenes(scenes) {
      this.scenes = scenes;
      const _sceneIndex = this.scenes.findIndex(
        (scene) =>
          new URL(scene.item.meta.html_url).pathname === this.$route.query.scene
      );

      const sceneData = this.scenes[_sceneIndex];
      this.startingPanorama = sceneData.item;
      this.activeScene = sceneData.scene;
      this.activeSceneView = sceneData.view;
      this.initialViewParameters = sceneData.initialViewParameters;
      this.activeSceneIndex = _sceneIndex;

      if (this.$route.query.hotspot) {
        // Find hotspot to open
        let child = null;
        let index = 0;

        const childContent = sceneData.item.child_content;

        for (let i = 0; i < sceneData.item.child_content.length; i++) {
          const pathname = new URL(childContent[i].html_url).pathname;
          if (pathname === this.$route.query.hotspot) {
            child = childContent[i];
            index = i;
          }
        }

        if (child) {
          this.clickChild(child, index, this.$route.query.hotspot);
        }
      }
    },
    async fetchPanoramas(startPanoramaId, panoramaChildren) {
      let _panoramaChildren = panoramaChildren;
      this.startingPanorama = await this.$store.dispatch(
        "wagtail/getStartPanorama",
        startPanoramaId
      );

      !_panoramaChildren &&
        (_panoramaChildren = this.startingPanorama.panorama_child_content);

      const data = await Promise.all(
        _panoramaChildren.map(async (item) => {
          return this.$store.dispatch("wagtail/getStartPanorama", item.id);
        })
      );

      this.childInstances = data;
      this.panoramas = data;
    },
    landingClosed() {

      this.accessibilityStream && (this.accessibilityStream = false);

      this.showLandingStreamfield = false;
      this.landingStreamfieldFlag = true;
      this.streamfieldLoaded(true);
      if (this.accessibilityMode == true) {
        this.accessibilityModalFlag = true;
      }

      if (this.roomGuideData.enabled) {
        this.foundChildren = this.roomGuideData.childPageIds
          .map((id) => this.startingPanorama.child_content.find((obj) => obj.id === id))
          .filter((obj) => obj !== undefined); // Remove any undefined objects
        if (this.foundChildren.length > 0) {
          this.manageRoomGuide();
        }
      }

    },
    manageRoomGuide() {
      if (this.foundChildren.length - 1 == this.roomGuideData.currentStep) {
        this.$store.dispatch("wagtail/toggleGuideEnabled");
        this.clickChild(this.foundChildren[this.roomGuideData.currentStep]);
      } else {
          setTimeout(() => {
          //Pause for 500ms to give video scene a chance to initialise if in use
          this.clickChild(this.foundChildren[this.roomGuideData.currentStep]);
          }, 500);
      }
    },
    showLandingTrigger() {
      this.landingStreamfieldFlag = false;
      this.showLandingStreamfield = true;
      if (this.accessibilityMode == true) {
        this.accessibilityModalFlag = false;
      }
    },
    welcomeOpened() {
      this.$emit("showLanding", this.startingPanoDataResult.id);
    },
    onFocus() {
      this.isMoving = true;
    },
    getChildTitle(child) {
      if (child.icon_only) return "";
      return child.title;
    },
    getAccessibilityChildTitle(child) {
      if (child.icon_only && child.chat_bubble_text && child.clickable) {
        return child.title;
      } else if (child.icon_only) {
        return "";
      } else {
        return child.title;
      }
    },
    getAriaLabel(child) {
      if (child.accessible_label) {
        return child.accessible_label;
      } else {
        return child.title;
      }
    },
    panoLoaded(scene) {
      this.scene = scene;
      this.checkZoomToChild();
    },
    welcomeClosed() {
      this.showLandingStreamfield = false;
      this.displayInstructions();
    },
    displayInstructions() {
      this.showInstructions = true;
      this.instructionsShown = true;
      let self = this;
      setTimeout(function () {
        self.showInstructions = false;
        self.ready = true;
      }, 3000);
    },
    async clickChild(child, i, hotspot) {
     
      if(!child) {
        return;
      }

      let isMoving = true;

      let zoomData = child.position_data;

      var destinationViewParameters = {
        yaw: zoomData.yaw,
        pitch: zoomData.pitch,
        fov: (40 * Math.PI) / 180,
      };

      var options = {
        transitionDuration: 1500,
      };

      this.scene.lookTo(destinationViewParameters, options);

      if (child.walkthrough_step_new) {

        const oldPanorama = this.startingPanorama.panorama;

        setTimeout(() => {
          const _sceneIndex = this.scenes.findIndex(
            (scene) => scene.item.id === child.walkthrough_step_new.panorama.id
          );


          const sceneData = this.scenes[_sceneIndex];
          this.startingPanorama = sceneData.item;
          this.activeScene = sceneData.scene;
          this.activeSceneView = sceneData.view;

          this.initialViewParameters =
            child.walkthrough_step_new.override_value;
          this.activeSceneIndex = _sceneIndex;

          this.videoSceneActive = false;

          const path = this.$route.path;
          this.$router.push(
            `${path}?scene=${new URL(sceneData.item.meta.html_url).pathname}`
          );
        }, options.transitionDuration);

        return;
      } else if (child.walkthrough_step) {
        setTimeout(() => {
          const _sceneIndex = this.scenes.findIndex(
            (scene) => scene.item.id === child.walkthrough_step.id
          );

          const sceneData = this.scenes[_sceneIndex];
          this.startingPanorama = sceneData.item;
          this.activeScene = sceneData.scene;
          this.activeSceneView = sceneData.view;
          this.initialViewParameters = sceneData.initialViewParameters;
          this.activeSceneIndex = _sceneIndex;

          const path = this.$route.path;
          this.$router.push(
            `${path}?scene=${new URL(sceneData.item.meta.html_url).pathname}`
          );
        }, options.transitionDuration);

        return;
      }

      // Track which hotspot or accessibility menu item has been most recently clicked. used later to return forcus to the right place.
      let currentUILocation = document.activeElement;
      if (
        currentUILocation.id == document.querySelectorAll("#hotspot-img")[0]?.id
      ) {
        this.currentActiveHotspot = currentUILocation;
        this.isMouseControl = false;
      } else if (currentUILocation.classList[0] == "v-image") {
        //reset focus location storage if mouse is used.
        this.currentActiveHotspot = undefined;
        this.currentActiveAccessibilityMenuItem = undefined;
        this.isMouseControl = true;
      } else if (currentUILocation.classList[0] == "accessibility-btn") {
        this.currentActiveAccessibilityMenuItem = currentUILocation;
        this.isMouseControl = false;
      } else {
        console.log(
          "Opening lightbox NO MATCH, new access method detected. update focus tracking script"
        );
      }

      //track previous camera position and hide the accessibility menu before opening the hotspot lightbox

      this.lastYaw = this.scene.view().yaw();
      this.lastPitch = this.scene.view().pitch();
      this.lastFov = this.scene.view().fov();
      this.useLastLocation = true;

      //Open URL
      this.checkZoomToChild();
      const path = this.$route.path;
      // console.log({path, qwe:new URL(child.html_url).pathname});
      // const childParsedUrl = new URL(child.html_url).pathname.split('/')[2]
      // this.$router.push(`${path}/${childParsedUrl}`);
      const hotpostUrl = hotspot || new URL(child.html_url).pathname;
      const scene = this.$route.query.scene;
      this.$router.push(
        `${path}?hotspot=${hotpostUrl}${scene ? `&scene=${scene}` : ""}`
      );
      this.selectedChild = child;

      const data = await this.$store.dispatch(
        "wagtail/getPageDetails",
        hotpostUrl
      );

      this.getLightboxData(data);

      if (isMoving === true) {
        this.accessibilityModalFlag = false;
      } else this.accessibilityModalFlag = true;
    },

    getLightboxData(child) {

      let id = child.id;
      let zoomData = child.position_data;
      this.lightboxReady = false;
      //TODO: I don't like using the store for this - should call the API service directly
      this.$store.dispatch("wagtail/getPopup", id);

      var destinationViewParameters = {
        yaw: zoomData.yaw,
        pitch: zoomData.pitch,
        fov: (40 * Math.PI) / 180,
      };

      var options = {
        transitionDuration: 1500,
      };

      this.scene.lookTo(destinationViewParameters, options);
      // Might be useful chose not to remove
      //Resetting Window, body and panorama scroll location to zero to counteract marzipano bug that moves the viewport offscreen
      // window.scrollTo(0, 0);
      // var bodyElement = document.querySelector("body");
      // bodyElement.scroll(0, 0);

      var panoramas = document.getElementsByClassName("panorama");
      for (let i = 0; i < panoramas.length; i++) {
        panoramas[i].scroll(0, 0);

      }

      let self = this;
      window.setTimeout(function () {
        self.lightboxReady = true;
        self.lightbox2Active = true;
      }, 1250);
    },

    closeLightbox() {
      //Updates trackers for hotspot status and reopens the accessibility menu if in accessibility mode.
      //this.lightboxActive = false;
      const path = this.$route.path;
      const scene = this.$route.query.scene;
      this.$router.push(`${path}${scene ? `?scene=${scene}` : ""}`);

      this.$store.commit("wagtail/getPopupSuccess", undefined);

      if(!this.roomGuideData.enabled) {

        /** Reset Camera */
        //Tracks that the camera is moving so that keyboard controls cannot override transition animation
        this.isMoving = true;

        let destinationViewParameters = {
            yaw: this.scene.view().yaw(),
            pitch: 0.0,
            fov: (60 * Math.PI) / 180,
          };

        // If we've flagged to use the last location, use it and clear the flag
        if (this.useLastLocation === true) {
          destinationViewParameters = {
            yaw: this.lastYaw,
            pitch: this.lastPitch,
            fov: this.lastFov,
          };
          this.useLastLocation = false;
        }

        var options = {
          transitionDuration: this.movementSpeed,
        };

        // Display the instructions if they've not been displayed before
        // This is to handle use cases where a user has navigated straight
        // to some lightbox content, and hasn't come via the traditional entry point

        if (this.instructionsShown === false) this.displayInstructions();
        this.scene.lookTo(destinationViewParameters, options);

        //Resetting Window, body and panorama scroll location to zero to counteract marzipano bug that moves the viewport offscreen
        // window.scrollTo(0, 0);
        // var bodyElement = document.querySelector("body");
        // bodyElement.scroll(0, 0);
        var panoramas = document.getElementsByClassName("panorama");
        for (let i = 0; i < panoramas.length; i++) {
          panoramas[i].scroll(0, 0);
          
        }

        //use stored values to return focus to the correct previous location in virtual room or accessibility menu
        const elements = document.querySelectorAll("#hotspot-img");

        if (
          this.currentActiveHotspot != undefined &&
          this.accessibilityMode == false
        ) {
          // Moves focus to previous hotspot location in virtual room
          this.currentActiveHotspot.focus();
        } else {
          if (this.accessibilityMode == false) {
            //Moves focus Back to the first hotspot if not using a mouse"
            if (this.isMouseControl == false) {
              elements[0].focus();
            }
          } else {
            if (this.currentActiveAccessibilityMenuItem != undefined) {
              //Moves focus Back to the previous accessibility menu item
              this.$nextTick(() => {
                this.currentActiveAccessibilityMenuItem.focus();
              });
            }
          }
        }
        /** End Of Reset Camera */
      }

    },

    checkZoomToChild() {
      if (
        this.startingPanorama &&
        this.startingPanorama.meta.type ===
        "engage_virtualrooms.EngageWalkthrough"
      ) {
        
        this.getLightboxData(this.selectedChild);
        return;
      }

      let currPath = window.location.pathname;
      let pagePath = new URL(this.startingPanorama.meta.html_url).pathname;

      //If we're not on the root page
      if (pagePath !== undefined && currPath !== undefined) {
        pagePath = pagePath.trimRight("/");
        currPath = currPath.trimRight("/");


        // If the requested path is not the same as the current page path, then we may be requesting a child object
        if (pagePath !== currPath) {
          // Search the children for a valid child

          for (let child of this.startingPanorama.child_content) {
            let childPath = new URL(child.html_url).pathname;
            if (childPath !== undefined) {

              childPath = childPath.trimRight("/");

              // We've found the right child, let's zoom to it and open the lightbox
              if (childPath === currPath) {

                this.getLightboxData(child);
                return;
              }
            }
          }
        }

      }
      // TODO: if we don't find a valid child, reset the scene or reload the page...
    },
    isRootPage() {
      let currPath = this.currentPath;
      let pagePath = new URL(this.startingPanorama.meta.html_url).pathname;
      if (currPath === pagePath) return true;
      return false;
    },
    shortcutClicked(url) {
      //When we click a shortcut, we either route to that page, or if the shortcut is the current page we close the dialog
      let pagePath = new URL(url).pathname;
      let currPath = this.currentPath;

      if (currPath === pagePath) this.welcomeClosed();
      else this.$router.push(pagePath);

      const firstFocusableElement =
        document.querySelectorAll("#hotspot-img")[0];
      firstFocusableElement.focus();
    },
    lookTo() {
      this.consoleYaw = this.formYaw;
      this.consolePitch = this.formPitch;
      this.$refs.marzipano.lookToConsole(this.consoleYaw, this.consolePitch);
    },
    accesibilityFlag() {
      return (this.accesibilityMode = true);
    },
    closeAccessibilityLightbox() {
      this.accessibilityFlag = false;
      this.accessibilityModalFlag = false;
      this.$store.commit(
        "wagtail/setAccessibiltyMode",
        this.accessibilityFlag,
        { root: true }
      );
      localStorage.setItem("accessibilityModeState", false);
      this.$emit("closeAccessibility");
    },
    accessibilityFlagChecker() {
      if (localStorage.getItem("accessibilityModeState") == "true") {
        this.accessibilityModalFlag = true;
        this.accessibilityFlag = true;
        this.$store.commit(
          "wagtail/setAccessibiltyMode",
          this.accessibilityModalFlag,
          { root: true }
        );

      } else {
        this.accessibilityModalFlag = false;
        this.accessibilityFlag = false;

        this.$store.commit(
          "wagtail/setAccessibiltyMode",
          this.accessibilityModalFlag,
          { root: true }
        );
      }
    },
  },
  watch: {
    currentQueryHotspot(value) {
      // If the 'hotspot' query parameter changes we open the lightbox data for that hotspot.
      if(value) {
        for (const child of this.startingPanorama.child_content) {
          const childPath = new URL(child.html_url).pathname
          if(value === childPath) {
            this.getLightboxData(child)
          }
        }
      }
    },
    roomGuideData: {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal.enabled) {
          this.manageRoomGuide();
        } else {
          this.$store.dispatch("wagtail/resetGuide");
        }
      },
    },
    accessibilityStream(val) {
      this.accessibilityModalFlag = !val;
      this.accessibilityFlag = !val;
      this.$store.commit(
        "wagtail/setAccessibiltyMode",
        this.accessibilityModalFlag,
        { root: true }
      );
    },
    activeSceneIndex(val) {

      if (this.streamfieldLoaded(null, true) === "true" && val != 0) {
        this.showLandingInfo = false;
        return;
      }

      if (val === 0) {
        this.showLandingInfo = true;
      } else this.showLandingInfo = false;
    },
    showLanding(val) {
      console.log(val, this.startingPanorama.id, "showlanding");
    },
    isMoving() {
      //Resetting Window, body and panorama scroll location to zero to counteract marzipano bug that moves the viewport offscreen
      // window.scrollTo(0, 0);
      //  var bodyElement = document.querySelector("body");
      //  bodyElement.scroll(0, 0);
      var panoramas = document.getElementsByClassName("panorama");
      for (let i = 0; i < panoramas.length; i++) {
        panoramas[i].scroll(0, 0);

      }

      if ("activeElement" in document) {
        const elements = document.querySelectorAll("#hotspot-img");
        if (document.activeElement.id == elements[0].id) {
          this.currentActiveHotspot = document.activeElement;
        }
      }
      if (this.isMoving) {
        arrowKeyNavigation.unregister();
        window.onkeydown = () => false;
        window.setTimeout(() => {
          this.isMoving = false;
        }, this.movementSpeed);
      } else {
        arrowKeyNavigation.register();
        window.onkeydown = () => true;

        //Resetting Window, body and panorama scroll location to zero to counteract marzipano bug that moves the viewport offscreen
        // window.scrollTo(0, 0);
        // var bodyElement = document.querySelector("body");
        // bodyElement.scroll(0, 0);
        for (let i = 0; i < panoramas.length; i++) {
          panoramas[i].scroll(0, 0);

        }
      }
    },
    accessibilityMode(newValue) {
      if (newValue == true) {
        this.accessibilityModalFlag = true;
        this.accessibilityFlag = true;
        localStorage.setItem("accessibilityModeState", true);
      } else {
        this.accessibilityModalFlag = false;
        this.accessibilityFlag = false;
        const elements = document.querySelectorAll("#hotspot-img");
        localStorage.setItem("accessibilityModeState", false);
        this.$nextTick(() => {
          elements[0].focus();
        });
      }
    },
    currentPath() {

      this.checkZoomToChild();
      //Hide the landing page if the path has changed
      if (!this.isRootPage() && this.showLanding) this.showLanding = false;

    },
    islightboxActive() {
      if (this.islightboxActive == false) {
        if (this.accessibilityMode == true) {
          this.accessibilityModalFlag = true;
        } else {
          this.accessibilityModalFlag = false;
        }
      } else {
        this.accesibilityModal = false;
      }
    },
    lightboxContent(res) {
      console.log(
        "current popup val changed",
        this.$store.state.wagtail.currentPopup,
        res
      );
    },
    lightboxReady(res) {
      console.log("lightbox ready", res);
    },
    accessibilityFlag(res) {
      console.log(res, "accessibility flag")
      if (!res) {
        this.accessibilityModalFlag = false
      } else this.accessibilityModalFlag = true
    }
  },
};
</script>

<style lang="scss" scoped>
.map-sidebar {
  position: absolute;
  top: 0px;
  left: 0px;
  /* width: 344px; */
  width: 500px;
  height: 100%;
}

.map {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  height: 100%;
}

.map-legend {
  display: none;
}

html {
  overflow: hidden !important;
}

.ignore-mouse {
  pointer-events: none !important;
}

.use-mouse {
  pointer-events: all;
}

.panorama {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
}

.button-panel {
  position: absolute;
  top: 0px;
  right: 0px;
}

.attribution-panel {
  position: absolute;
  bottom: 0px;
  left: 0px;
}

@media (max-width: 600px) {
  .button-panel {
    bottom: 0px;
  }
}

.secondary-logo-panel {
  position: absolute;
  bottom: 0px;
  right: 0px;
  max-height: 96px;
}

.lightbox-btn:focus-visible {
  outline: 4px dashed darkorange;
}

.accessibility-btn:focus-visible {
  outline: 4px dashed darkorange;
}

.accessibility-header {
  background-color: black;
  color: white;
}

::v-deep .accessibility-lightbox {
  min-width: 300px;
  width: 25vw;
}

::v-deep .accessibility-lightbox.v-dialog:not(.v-dialog--fullscreen) {
  max-height: 70% !important;
}

::v-deep .accessibility-lightbox.v-dialog:not(.v-dialog--fullscreen).mobile-browser {
  max-height: calc(70% - 50px) !important;
}
</style>
